/**
 * @module app.core
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { I18N } from 'aurelia-i18n';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/essette-api';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { ImportStatusAndHistoryReloadEvent } from 'app/um/lib/events';
import { ImportsService, IImportHistorySearchFilters } from 'app/core/services/imports-service';
import { DateValue } from 'app/shared/lib/date-time';
import * as Core from 'app/core/models/essette-api';
import { guid } from 'app/shared/lib/guid';

@autoinject()
export class ImportStatusAndHistoryPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected importJobs: ReadonlyArray<Core.ImportJob> = [];
    protected importStatuses: ReadonlyArray<Core.ImportStatus> = [];
    protected importTenants: Core.ImportTenant[] = [];
    protected policyProfiles: Core.PolicyProfile[] = [];
    protected policySections: Core.PolicySection[] = [];

    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    public importHistorySearchFilters: IImportHistorySearchFilters = {};

    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    public tempImportHistorySearchFilters: IImportHistorySearchFilters = {};

    protected selectedStatuses: Core.ImportStatus[] = [];
    protected selectedTenants: Core.ImportTenant[] = [];
    protected selectedProfiles: Core.PolicyProfile[] = [];
    protected selectedSections: Core.PolicySection[] = [];

    protected tempSelectedStatuses: Core.ImportStatus[] = [];
    protected tempSelectedTenants: Core.ImportTenant[] = [];
    protected tempSelectedProfiles: Core.PolicyProfile[] = [];
    protected tempSelectedSections: Core.PolicySection[] = [];


    private importStatusAndHistoryReload: Subscription | null = null;

    protected resultCount = 0;
    protected pageSize = 30;

    protected id: guid = guid();

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: ImportsService,
        private readonly events: EventAggregator,
        private readonly i18n: I18N,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);

    }

    protected bind(): void {
        this.importStatusAndHistoryReload = this.events.subscribe(ImportStatusAndHistoryReloadEvent, (e: ImportStatusAndHistoryReloadEvent) => {
            this.reload();
        });

        this.setDefaultDateFilters();
        this.loadTenants();
        this.loadProfiles();
        this.loadSections();
        this.loadImportStatuses();
    }

    protected detached(): void {
        if(this.importStatusAndHistoryReload) {
            this.importStatusAndHistoryReload.dispose();
        }
    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        this.loadImportHistory()
    }

    protected submitSearch() {
        this.importHistorySearchFilters = { ...this.tempImportHistorySearchFilters };

        this.selectedStatuses = [...this.tempSelectedStatuses];
        this.selectedTenants = [...this.tempSelectedTenants];
        this.selectedProfiles = [...this.tempSelectedProfiles];
        this.selectedSections = [...this.tempSelectedSections];

        this.search();
    }

    protected buildSelectedProfiles(): string[] {
        const profiles: string[] = [];
        this.selectedProfiles.forEach(item => {
            if(item.key) {
                profiles.push(item.key);
            }//:null and undefined need to be filtered out.
        })
        return profiles;
    }

    protected buildSelectedSections(): string[] {
        const sections: string[] = [];
        this.selectedSections.forEach(item => {
            if(item.key) {
                sections.push(item.key);
            }
        })
        return sections;
    }

    protected buildSelectedTenants(): string[] {
        const tenants: string[] = [];
        this.selectedTenants.forEach(item => {
            if(item.key) {
                tenants.push(item.key);
            }
        })
        return tenants;
    }

    protected buildSelectedStatuses(): Core.ImportStatus[] {
        const statuses: Core.ImportStatus[] = [];
        this.selectedStatuses.forEach(item => {
            if(item.id) {
                statuses.push({ id: item.id });
            }//:A value for exactly one of 'id', 'url', or 'code' is required.
            //Cannot pass in the default Core.ImportStatus JSON with all fields
        })
        return statuses;
    }

    protected setDefaultDateFilters() {
        const today = this.env.today;
        this.importHistorySearchFilters.jobCompletedStartDate = (new DateValue(today)).add(-3, 'days').toString();
    }

    protected buildUnknownTenant(): Core.ImportTenant {
        const tenant: Core.ImportTenant = {
            $display: `${this.i18n.tr('ui:labels.unknown', { what: this.i18n.tr('core:importHistory.job.tenant.$') })}`, key: 'TU'
        };
        return tenant;
    }

    protected buildUnknownProfile(): Core.PolicyProfile {
        const profile: Core.PolicyProfile = {
            $display: `${this.i18n.tr('ui:labels.unknown', { what: this.i18n.tr('core:importHistory.job.profile.$') })}`, key: 'PU'
        };
        return profile;
    }

    protected buildUnknownSection(): Core.PolicySection {
        const section: Core.PolicySection = {
            $display: `${this.i18n.tr('ui:labels.unknown', { what: this.i18n.tr('core:importHistory.job.tenant.table.section.$long') })}`, key: 'SU'
        };
        return section;
    }

    protected search(): void {
        this.$pager.page = 0;

        if(!!this.importHistorySearchFilters.jobCompletedStartDate ||
            !!this.importHistorySearchFilters.jobCompletedEndDate ||
            !!this.importHistorySearchFilters.jobStagedStartDate ||
            !!this.importHistorySearchFilters.jobStagedEndDate ||
            !!this.importHistorySearchFilters.stagedJobIdentifier ||
            this.selectedStatuses.length > 0 ||
            this.selectedTenants.length > 0 ||
            this.selectedProfiles.length > 0 ||
            this.selectedSections.length > 0) {
            this.loadImportHistory();
        } else {
            this.toaster.standard.searchNoCriteria();
        }
    }

    protected clear(): void {
        this.importHistorySearchFilters = {};
        this.selectedStatuses = [];
        this.selectedTenants = [];
        this.selectedProfiles = [];
        this.selectedSections = [];

        this.tempImportHistorySearchFilters = {};
        this.tempSelectedStatuses = [];
        this.tempSelectedTenants = [];
        this.tempSelectedProfiles = [];
        this.tempSelectedSections = [];
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    private async loadImportHistory(): Promise<void> {
        const queryParams: Partial<IImportHistorySearchFilters> = {
            jobCompletedStartDate: this.importHistorySearchFilters.jobCompletedStartDate,
            jobCompletedEndDate: this.importHistorySearchFilters.jobCompletedEndDate,
            jobStagedStartDate: this.importHistorySearchFilters.jobStagedStartDate,
            jobStagedEndDate: this.importHistorySearchFilters.jobStagedEndDate,
            stagedJobIdentifier: this.importHistorySearchFilters.stagedJobIdentifier || undefined,
            statuses: this.selectedStatuses.length > 0 ? this.buildSelectedStatuses() : undefined,
            tenantKeys: this.selectedTenants.length > 0 ? this.buildSelectedTenants() : undefined,
            profileKeys: this.selectedProfiles.length > 0 ? this.buildSelectedProfiles() : undefined,
            sectionKeys: this.selectedSections.length > 0 ? this.buildSelectedSections() : undefined,

            orderBy: {
                jobs: [{
                    property: "control.stagedTimestamp",
                    descending: true
                }]
            },
            paging: {
                top: this.pageSize,
                skip: this.$pager?.page * this.pageSize,
            }
        };
        let isSuccessful = false;
        let errorMessage: string = "";
        try {
            this.state.beginLoad();

            const response = await this.service.loadImportHistory(queryParams);
            isSuccessful = response.isSuccessful;
            this.importJobs = response.result.jobs;
            this.resultCount = response.result.count || 0;
        } catch(error) {
            if(error.description) { errorMessage = error.description; }
            throw error;
        } finally {
            if(!isSuccessful) {
                const exception = (this.toaster.toast())
                    .title('Detail')
                    .class('ess-toast-error')
                    .icon('ui:icons.error')
                    .message(errorMessage)
                exception.show();
            }
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadImportStatuses(): Promise<void> {
        try {
            this.state.beginLoad();
            const response = await this.service.loadImportStatuses();
            this.importStatuses = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadTenants(): Promise<void> {
        try {
            this.state.beginLoad();
            const response = await this.service.loadImportTenants();
            this.importTenants = response.results;
            if(this.env.user.hasGlobalAccess) {
                this.importTenants.unshift(this.buildUnknownTenant());
            }
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadProfiles(): Promise<void> {
        try {
            this.state.beginLoad();
            const response = await this.service.loadPolicyProfiles();
            this.policyProfiles = response.results;
            this.policyProfiles.unshift(this.buildUnknownProfile());
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadSections(): Promise<void> {
        try {
            this.state.beginLoad();
            const response = await this.service.loadPolicySections();
            this.policySections = response.results;
            this.policySections.unshift(this.buildUnknownSection());
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

}
